import { useState, useEffect, lazy, Suspense, type CSSProperties, type ReactNode } from 'react';
import {
  useTracks,
  useConnectionState,
  useIsSpeaking,
  VideoTrack,
  type TrackReference,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track, ConnectionState, type RoomOptions, type RemoteParticipant } from 'livekit-client';
import { useWsEvents } from '../hooks/useWsEvents';
import type { CameraBorderColor, ParticipantTransform, VideoTransformsMap, OverlayLayout, PipPosition } from '@sleeved/lib';
import { DEFAULT_PIP_POSITION, DEFAULT_SHOW_CAMERA_FRAMES, DEFAULT_CAMERA_BORDER_COLOR } from '@sleeved/lib';

// ---------------------------------------------------------------------------
// Dynamic import for LiveKitRoom only
// ---------------------------------------------------------------------------
const LiveKitRoom = lazy(() =>
  import('@livekit/components-react').then((m) => ({ default: m.LiveKitRoom })),
);

// ---------------------------------------------------------------------------
// StreamView — Broadcast-ready overlay layout for OBS chroma key compositing
// ---------------------------------------------------------------------------
const OVERLAY_ROOM_OPTIONS: RoomOptions = {
  adaptiveStream: true,
  dynacast: true,
};

// Allowlist for the ?bg= query param to prevent CSS injection.
const BG_ALLOWLIST = /^(transparent|black|white|#[0-9a-fA-F]{3,8})$/;

// Brand gradient and glow constants
const BRAND_GRADIENT = 'linear-gradient(135deg, #FF865C, #FF5CA0, #865CFF)';
const BRAND_GLOW = '#FF865C';

// ---------------------------------------------------------------------------
// BorderValues — resolved CSS values for border and glow, derived once in
// StreamViewInner and threaded down as a plain object (no union type threading)
// ---------------------------------------------------------------------------
interface BorderValues {
  /** CSS value for the border — hex string for solid, linear-gradient(...) for gradient/brand */
  border: string;
  /** Hex colour to use for box-shadow glow */
  glow: string;
  /** Whether the border is a CSS gradient (requires wrapper+child technique) */
  isGradient: boolean;
}

function resolveBorderValues(cameraBorderColor: CameraBorderColor): BorderValues {
  switch (cameraBorderColor.mode) {
    case 'brand':
      return { border: BRAND_GRADIENT, glow: BRAND_GLOW, isGradient: true };
    case 'solid':
      return { border: cameraBorderColor.color, glow: cameraBorderColor.color, isGradient: false };
    case 'gradient': {
      const stops = cameraBorderColor.stops;
      const css = `linear-gradient(135deg, ${stops.join(', ')})`;
      const glow = stops[0] ?? BRAND_GLOW;
      return { border: css, glow, isGradient: true };
    }
  }
}

// Evaluated once at module load — the overlay URL never changes mid-session.
const PREFERS_REDUCED_MOTION =
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

export function StreamView() {
  const [livekitToken, setLivekitToken] = useState<string | null>(null);
  const [sseToken, setSseToken] = useState<string | null>(null);
  const [bgColor, setBgColor] = useState('transparent');
  const serverUrl = import.meta.env.VITE_LIVEKIT_URL ?? '';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lk = params.get('livekit');
    const sse = params.get('token');
    const bg = params.get('bg');
    if (lk) setLivekitToken(lk);
    if (sse) setSseToken(sse);
    if (bg) setBgColor(BG_ALLOWLIST.test(bg) ? bg : 'transparent');
  }, []);

  if (!livekitToken || !serverUrl) {
    return (
      <div style={{ color: '#fff', padding: '20px', background: 'transparent' }}>
        Waiting for LiveKit token...
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div style={{ color: '#fff', padding: '20px', background: 'transparent' }}>
          Loading stream...
        </div>
      }
    >
      <div
        style={{
          width: '100vw',
          height: '100vh',
          background: bgColor, // 'transparent' for OBS chroma key, 'black' for iframe preview
          overflow: 'hidden',
        }}
      >
        <LiveKitRoom serverUrl={serverUrl} token={livekitToken} connect={true} options={OVERLAY_ROOM_OPTIONS}>
          <StreamViewInner sseToken={sseToken} />
        </LiveKitRoom>
      </div>
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Inner component — must be inside LiveKitRoom for hooks
// ---------------------------------------------------------------------------
function StreamViewInner({ sseToken }: { sseToken: string | null }) {
  const connectionState = useConnectionState();
  const cameraTracks = useTracks([Track.Source.Camera], { onlySubscribed: true });

  // WS-driven state (video transforms + layout mode + border color + player identities)
  // IMPORTANT: eventNamesCsv must be a string literal — computed/joined values
  // cause the useWsEvents effect to re-run and reconnect on every render.
  const { data: sseData } = useWsEvents<{
    videoTransforms?: VideoTransformsMap;
    overlayLayout?: OverlayLayout;
    pipPosition?: PipPosition;
    showCameraFrames?: boolean;
    cameraBorderColor?: CameraBorderColor;
    p1Identity?: string | null;
    p2Identity?: string | null;
  }>(
    'videoTransforms,overlayLayout,pipPosition,showCameraFrames,cameraBorderColor,playerWidget',
    {
      videoTransforms: {},
      overlayLayout: 'side-by-side',
      pipPosition: DEFAULT_PIP_POSITION,
      showCameraFrames: DEFAULT_SHOW_CAMERA_FRAMES,
      cameraBorderColor: DEFAULT_CAMERA_BORDER_COLOR,
      p1Identity: null,
      p2Identity: null,
    },
    sseToken ?? undefined,
  );

  const videoTransforms = sseData.videoTransforms ?? {};
  const layout = sseData.overlayLayout ?? 'side-by-side';
  const pipPosition = sseData.pipPosition ?? DEFAULT_PIP_POSITION;
  const showCameraFrames = sseData.showCameraFrames ?? DEFAULT_SHOW_CAMERA_FRAMES;

  // Resolve border values once here — layouts receive a plain { border, glow, isGradient }
  // object instead of the raw CameraBorderColor union, avoiding repeated switch logic downstream
  const borderValues = resolveBorderValues(sseData.cameraBorderColor ?? DEFAULT_CAMERA_BORDER_COLOR);

  // ---------------------------------------------------------------------------
  // Identity-based track resolution
  // Assigns camera tracks to P1 (index 0) and P2 (index 1) slots by matching
  // participant.identity against p1Identity/p2Identity from the playerWidget event.
  // Falls back to arrival-order when identities are unset or no match is found.
  // ---------------------------------------------------------------------------
  const resolvedTracks = resolveTracksByIdentity(
    cameraTracks,
    sseData.p1Identity ?? null,
    sseData.p2Identity ?? null,
  );

  // Loading states
  if (connectionState === ConnectionState.Connecting) {
    return (
      <div style={{ color: '#fff', padding: '20px', background: 'transparent' }}>
        Connecting to room...
      </div>
    );
  }

  if (connectionState === ConnectionState.Disconnected) {
    return (
      <div style={{ color: '#fff', padding: '20px', background: 'transparent' }}>
        Disconnected from room.
      </div>
    );
  }

  // Render correct layout
  if (layout === 'pip') {
    return (
      <PIPLayout
        cameraTracks={resolvedTracks}
        videoTransforms={videoTransforms}
        pipPosition={pipPosition}
        showCameraFrames={showCameraFrames}
        borderValues={borderValues}
      />
    );
  }

  if (layout === 'top-bottom') {
    return (
      <TopBottomLayout
        cameraTracks={resolvedTracks}
        videoTransforms={videoTransforms}
        showCameraFrames={showCameraFrames}
        borderValues={borderValues}
      />
    );
  }

  return (
    <SideBySideLayout
      cameraTracks={resolvedTracks}
      videoTransforms={videoTransforms}
      showCameraFrames={showCameraFrames}
      borderValues={borderValues}
    />
  );
}

// ---------------------------------------------------------------------------
// Side-by-side layout (default)
// ---------------------------------------------------------------------------
interface LayoutProps {
  cameraTracks: TrackReference[];
  videoTransforms: VideoTransformsMap;
  showCameraFrames: boolean;
  borderValues: BorderValues;
}

function SideBySideLayout({ cameraTracks, videoTransforms, showCameraFrames, borderValues }: LayoutProps) {
  // Take first 2 camera tracks
  const [p1Track, p2Track] = cameraTracks.slice(0, 2);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '8px',
        position: 'relative',
      }}
    >
      {/* Player 1 */}
      {p1Track && (
        <PlayerPanel
          trackRef={p1Track}
          transform={videoTransforms[p1Track.participant.identity]}
          prefersReducedMotion={PREFERS_REDUCED_MOTION}
          showFrames={showCameraFrames}
          borderValues={borderValues}
        />
      )}

      {/* Player 2 */}
      {p2Track && (
        <PlayerPanel
          trackRef={p2Track}
          transform={videoTransforms[p2Track.participant.identity]}
          prefersReducedMotion={PREFERS_REDUCED_MOTION}
          showFrames={showCameraFrames}
          borderValues={borderValues}
        />
      )}

      {/* Logo watermark */}
      <img
        src="/favicon_512.png"
        alt=""
        style={{
          position: 'absolute',
          bottom: '16px',
          right: '16px',
          width: '48px',
          height: '48px',
          opacity: 0.4,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PIP layout (picture-in-picture)
// ---------------------------------------------------------------------------
interface PIPLayoutProps extends LayoutProps {
  pipPosition: PipPosition;
}

// ---------------------------------------------------------------------------
// GradientBorderWrapper — outer gradient shell + inner clip for gradient borders
// ---------------------------------------------------------------------------
interface GradientBorderWrapperProps {
  gradient: string;
  borderRadius: number;
  children: ReactNode;
  style?: CSSProperties;
}

function GradientBorderWrapper({ gradient, borderRadius, children, style }: GradientBorderWrapperProps) {
  return (
    <div
      style={{
        background: gradient,
        borderRadius: `${borderRadius}px`,
        padding: '3px',
        boxSizing: 'border-box',
        ...style,
      }}
    >
      <div
        style={{
          borderRadius: `${borderRadius - 3}px`,
          overflow: 'hidden',
          width: '100%',
          height: '100%',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Maps a PipPosition value to absolute CSS anchor properties for the PIP window.
 */
function getPipAnchor(position: PipPosition): CSSProperties {
  switch (position) {
    case 'top-left':     return { top: '16px', left: '16px' };
    case 'top-right':    return { top: '16px', right: '16px' };
    case 'bottom-left':  return { bottom: '16px', left: '16px' };
    case 'bottom-right':
    default:             return { bottom: '16px', right: '16px' };
  }
}

/**
 * When the PIP window is in a bottom corner, place the logo in the opposite
 * bottom corner to avoid overlap. For top-corner PIP, keep logo bottom-right.
 */
function getLogoAnchor(position: PipPosition): CSSProperties {
  if (position === 'bottom-left')  return { bottom: '16px', right: '16px' };
  if (position === 'bottom-right') return { bottom: '16px', left: '16px' };
  // Top positions: logo stays bottom-right (away from PIP)
  return { bottom: '16px', right: '16px' };
}

function PIPLayout({ cameraTracks, videoTransforms, pipPosition, showCameraFrames, borderValues }: PIPLayoutProps) {
  const [primaryTrack, pipTrack] = cameraTracks.slice(0, 2);
  const pipAnchor = getPipAnchor(pipPosition);
  const logoAnchor = getLogoAnchor(pipPosition);

  // PIP container positioning and transform (applied to the outer wrapper)
  const pipPositionStyle: CSSProperties = {
    position: 'absolute',
    width: '30%',
    aspectRatio: '16 / 9',
    zIndex: 20,
    ...pipAnchor,
    ...getTransformStyle(videoTransforms[pipTrack?.participant.identity ?? '']),
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      {/* Primary player fills frame */}
      {primaryTrack && (
        <div style={{ width: '100%', height: '100%' }}>
          <PlayerPanel
            trackRef={primaryTrack}
            transform={videoTransforms[primaryTrack.participant.identity]}
            prefersReducedMotion={PREFERS_REDUCED_MOTION}
            fullHeight={true}
            showFrames={showCameraFrames}
            borderValues={borderValues}
          />
        </div>
      )}

      {/* PIP overlay — 30% width, 16:9 aspect ratio */}
      {pipTrack && showCameraFrames && borderValues.isGradient && (
        <GradientBorderWrapper
          gradient={borderValues.border}
          borderRadius={12}
          style={{
            ...pipPositionStyle,
            boxShadow: `0 4px 12px rgba(0, 0, 0, 0.6)`,
          }}
        >
          <VideoTrack
            trackRef={pipTrack}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <PipSpeakingOverlay
            participant={pipTrack.participant as RemoteParticipant}
            prefersReducedMotion={PREFERS_REDUCED_MOTION}
            glowColor={borderValues.glow}
          />
        </GradientBorderWrapper>
      )}
      {pipTrack && showCameraFrames && !borderValues.isGradient && (
        <div
          style={{
            ...pipPositionStyle,
            border: `3px solid ${borderValues.border}`,
            borderRadius: '12px',
            boxShadow: `0 4px 12px rgba(0, 0, 0, 0.6)`,
            overflow: 'hidden',
          }}
        >
          <VideoTrack
            trackRef={pipTrack}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <PipSpeakingOverlay
            participant={pipTrack.participant as RemoteParticipant}
            prefersReducedMotion={PREFERS_REDUCED_MOTION}
            glowColor={borderValues.glow}
          />
        </div>
      )}
      {pipTrack && !showCameraFrames && (
        <div
          style={{
            ...pipPositionStyle,
            overflow: 'hidden',
          }}
        >
          <VideoTrack
            trackRef={pipTrack}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      )}

      {/* Logo watermark — moves to opposite corner from PIP when PIP is in a bottom corner */}
      <img
        src="/favicon_512.png"
        alt=""
        style={{
          position: 'absolute',
          width: '48px',
          height: '48px',
          opacity: 0.4,
          pointerEvents: 'none',
          ...logoAnchor,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// TopBottomPanel — 16:9 framed video for top-bottom layout
// ---------------------------------------------------------------------------
function TopBottomPanel({
  trackRef,
  transform,
  showFrames = true,
  borderValues,
}: {
  trackRef: TrackReference;
  transform?: ParticipantTransform;
  showFrames?: boolean;
  borderValues: BorderValues;
}) {
  const participant = trackRef.participant as RemoteParticipant;
  const isSpeaking = useIsSpeaking(participant);

  const baseStyle: CSSProperties = {
    height: '100%',
    aspectRatio: '16 / 9',
    maxWidth: '100%',
    position: 'relative',
    overflow: 'hidden',
    ...getTransformStyle(transform),
  };

  if (!showFrames) {
    return (
      <div style={baseStyle}>
        <VideoTrack
          trackRef={trackRef}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
    );
  }

  if (borderValues.isGradient) {
    return (
      <GradientBorderWrapper
        gradient={borderValues.border}
        borderRadius={16}
        style={{
          height: '100%',
          aspectRatio: '16 / 9',
          maxWidth: '100%',
          ...getTransformStyle(transform),
        }}
      >
        <VideoTrack
          trackRef={trackRef}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {isSpeaking && !PREFERS_REDUCED_MOTION && <SpeakingBorder glowColor={borderValues.glow} />}
      </GradientBorderWrapper>
    );
  }

  return (
    <div
      style={{
        ...baseStyle,
        border: `3px solid ${borderValues.border}`,
        borderRadius: '16px',
        background: 'rgba(0, 0, 0, 0.5)',
      }}
    >
      <VideoTrack
        trackRef={trackRef}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {isSpeaking && !PREFERS_REDUCED_MOTION && <SpeakingBorder glowColor={borderValues.glow} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-bottom layout (stacked vertically, 16:9 per half)
// ---------------------------------------------------------------------------
function TopBottomLayout({ cameraTracks, videoTransforms, showCameraFrames, borderValues }: LayoutProps) {
  const [p1Track, p2Track] = cameraTracks.slice(0, 2);

  // Single player: center in the full viewport
  if (p1Track && !p2Track) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <TopBottomPanel
          trackRef={p1Track}
          transform={videoTransforms[p1Track.participant.identity]}
          borderValues={borderValues}
        />
        <img
          src="/favicon_512.png"
          alt=""
          style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            width: '48px',
            height: '48px',
            opacity: 0.4,
            pointerEvents: 'none',
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Player 1 — top half */}
      {p1Track && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <TopBottomPanel
            trackRef={p1Track}
            transform={videoTransforms[p1Track.participant.identity]}
            showFrames={showCameraFrames}
            borderValues={borderValues}
          />
        </div>
      )}

      {/* Player 2 — bottom half */}
      {p2Track && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <TopBottomPanel
            trackRef={p2Track}
            transform={videoTransforms[p2Track.participant.identity]}
            showFrames={showCameraFrames}
            borderValues={borderValues}
          />
        </div>
      )}

      {/* Logo watermark */}
      <img
        src="/favicon_512.png"
        alt=""
        style={{
          position: 'absolute',
          bottom: '16px',
          right: '16px',
          width: '48px',
          height: '48px',
          opacity: 0.4,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Player panel (video + name tag + speaking border)
// ---------------------------------------------------------------------------
// PIP speaking overlay — isolated component so useIsSpeaking has a guaranteed participant
function PipSpeakingOverlay({
  participant,
  prefersReducedMotion,
  glowColor,
}: {
  participant: RemoteParticipant;
  prefersReducedMotion: boolean;
  glowColor: string;
}) {
  const isSpeaking = useIsSpeaking(participant);
  if (!isSpeaking || prefersReducedMotion) return null;
  return <SpeakingBorder glowColor={glowColor} />;
}

interface PlayerPanelProps {
  trackRef: TrackReference;
  transform?: ParticipantTransform;
  prefersReducedMotion: boolean;
  fullHeight?: boolean;
  showFrames?: boolean;
  borderValues: BorderValues;
}

function PlayerPanel({
  trackRef,
  transform,
  prefersReducedMotion,
  fullHeight = false,
  showFrames = true,
  borderValues,
}: PlayerPanelProps) {
  const participant = trackRef.participant as RemoteParticipant;
  const isSpeaking = useIsSpeaking(participant);
  const containerStyle: CSSProperties = fullHeight
    ? {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
      };

  // Dimensions for the video container
  const videoDimensions: CSSProperties = {
    position: 'relative',
    width: '100%',
    flex: fullHeight ? 1 : undefined,
    aspectRatio: fullHeight ? undefined : '16 / 9',
    overflow: 'hidden',
    ...getTransformStyle(transform),
  };

  const videoContent = (
    <>
      <VideoTrack
        trackRef={trackRef}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {showFrames && isSpeaking && !prefersReducedMotion && (
        <SpeakingBorder glowColor={borderValues.glow} />
      )}
    </>
  );

  return (
    <div style={containerStyle}>
      {/* Video container — frames rendered with gradient wrapper or solid border */}
      {showFrames && borderValues.isGradient ? (
        <GradientBorderWrapper
          gradient={borderValues.border}
          borderRadius={16}
          style={{
            width: '100%',
            flex: fullHeight ? 1 : undefined,
            aspectRatio: fullHeight ? undefined : '16 / 9',
            ...getTransformStyle(transform),
          }}
        >
          <VideoTrack
            trackRef={trackRef}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {isSpeaking && !prefersReducedMotion && (
            <SpeakingBorder glowColor={borderValues.glow} />
          )}
        </GradientBorderWrapper>
      ) : (
        <div
          style={{
            ...videoDimensions,
            ...(showFrames ? {
              border: `3px solid ${borderValues.border}`,
              borderRadius: '16px',
              background: 'rgba(0, 0, 0, 0.5)',
            } : {}),
          }}
        >
          {videoContent}
        </div>
      )}

    </div>
  );
}

// ---------------------------------------------------------------------------
// Speaking border animation — glow color follows the resolved border color
// ---------------------------------------------------------------------------
function SpeakingBorder({ glowColor }: { glowColor: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        border: `4px solid ${glowColor}`,
        borderRadius: 'inherit',
        boxShadow: `0 0 24px ${glowColor}`,
        animation: 'speaking-pulse 2s ease-in-out infinite',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Identity-based track resolution
// ---------------------------------------------------------------------------
/**
 * Resolves camera tracks into a [P1, P2] ordered array based on participant
 * identity strings received from the playerWidget WebSocket event.
 *
 * Slot semantics: index 0 = P1 (full-screen/left/top), index 1 = P2.
 *
 * Rules:
 * - When both identities are null or no match is found, fall back to arrival order.
 * - Duplicate identity guard: if p1Identity === p2Identity (non-null), P2 slot is
 *   left empty to avoid rendering the same track twice.
 * - Single-participant promotion: when only one participant is connected, they are
 *   always placed at index 0 regardless of which identity slot they matched. This
 *   is required because layout components expect index 0 for centering / full-screen.
 * - Partial-match behavior: when one identity is null, that slot falls back to the
 *   next available unmatched participant (arrival order for the unset slot).
 */
function resolveTracksByIdentity(
  cameraTracks: TrackReference[],
  p1Identity: string | null,
  p2Identity: string | null,
): TrackReference[] {
  const available = cameraTracks.slice(0, 2);

  // No identities configured — fall back to arrival order
  if (!p1Identity && !p2Identity) return available;

  // Duplicate identity guard — only fill P1, leave P2 empty
  const isDuplicate = p1Identity != null && p1Identity === p2Identity;

  let p1Track: TrackReference | undefined;
  let p2Track: TrackReference | undefined;

  for (const track of available) {
    const identity = track.participant.identity;
    if (!p1Track && p1Identity && identity === p1Identity) {
      p1Track = track;
    } else if (!p2Track && !isDuplicate && p2Identity && identity === p2Identity) {
      p2Track = track;
    }
  }

  // If no matches at all, fall back to arrival order
  if (!p1Track && !p2Track) return available;

  // Single-participant promotion: one connected participant always goes to index 0
  if (available.length === 1) {
    // At this point, we know at least one identity matched (else we would have returned
    // 'available' on line 828). So either p1Track or p2Track must be set.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- safe because the early return above (`!p1Track && !p2Track`) rules out both being undefined; if `p1Track` is falsy here, `p2Track` must be set.
    return [p1Track ?? p2Track!];
  }

  // Build the resolved pair
  const result: TrackReference[] = [];

  if (p1Track) {
    result.push(p1Track);
  } else {
    // P1 identity not found among participants — use unmatched track if available
    const unmatched = available.find(
      (t) => t !== p2Track,
    );
    if (unmatched) result.push(unmatched);
  }

  if (p2Track && !isDuplicate) {
    result.push(p2Track);
  } else if (!isDuplicate) {
    // P2 identity not found — use remaining unmatched track
    const unmatched = available.find(
      (t) => t !== p1Track && !result.includes(t),
    );
    if (unmatched) result.push(unmatched);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Transform utility (rotation + mirror + position offset + user scale)
// ---------------------------------------------------------------------------
function getTransformStyle(transform?: ParticipantTransform): CSSProperties {
  if (!transform) return {};

  const { rotation, mirrorH, mirrorV, offsetX, offsetY, scale } = transform;
  const transforms: string[] = [];

  // translate first in string = applied last (CSS right-to-left) = screen-space offset
  if (offsetX || offsetY) transforms.push(`translate(${offsetX ?? 0}px, ${offsetY ?? 0}px)`);
  if (rotation !== 0) transforms.push(`rotate(${rotation}deg)`);
  if (mirrorH) transforms.push('scaleX(-1)');
  if (mirrorV) transforms.push('scaleY(-1)');
  if ((scale ?? 1) !== 1) transforms.push(`scale(${scale ?? 1})`);

  return transforms.length > 0 ? { transform: transforms.join(' ') } : {};
}

// ---------------------------------------------------------------------------
// Global styles for speaking-pulse animation (distinct from animations.css pulse)
// Guarded against duplicate injection on HMR reloads.
// ---------------------------------------------------------------------------
if (!document.getElementById('speaking-pulse-style')) {
  const style = document.createElement('style');
  style.id = 'speaking-pulse-style';
  style.textContent = `
    @keyframes speaking-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }
  `;
  document.head.appendChild(style);
}
